import "core-js/modules/esnext.iterator.constructor.js";
import "core-js/modules/esnext.iterator.find.js";
import { mapGetters } from 'vuex';
import { registerTenantAccessPrompt, requestEntry, takeTenantEntryLink } from '@/utils/tenant-access-prompt';
import LookupSelect from '@/components/elements/lookup-select.vue';

/**
 * A global admin felugró ablaka: belépés egy intézménybe és az írás bekapcsolása, egy helyen. Az írás akkor kérődik,
 * amikor a szerver egy mentést `tenant_read_only`-val utasít el: bekapcsolás után az eredeti kérés magától újra lefut.
 * A belépés egy `?tenant=<id>` (és `&write=1`) linkre érkezve kérődik (a router-őr jegyzi meg), utána a céllap az új
 * intézménnyel töltődik be.
 * A gyökérben ül, hogy minden képernyőn működjön.
 */
export default {
  name: 'tenant-access-prompt',
  components: {
    LookupSelect
  },
  data() {
    return {
      show: false,
      request: null,
      reason: '',
      minutes: 60,
      error: '',
      busy: false,
      resolve: null,
      chosenTenantId: null
    };
  },
  computed: {
    ...mapGetters({
      tenants: 'tenant/list',
      current: 'tenant/current',
      requiresEntry: 'tenant/requiresEntry'
    }),
    isEnter() {
      return this.request?.type === 'enter';
    },
    // intézmény nélküli link: a felhasználó választja ki, hová lép be
    isChoose() {
      return this.isEnter && !this.request?.tenantId;
    },
    enterTenantId() {
      return this.isChoose ? this.chosenTenantId : this.request?.tenantId;
    },
    needsWrite() {
      return !this.isEnter || !!this.request?.withWrite;
    },
    targetName() {
      const id = this.isEnter ? this.enterTenantId : this.current;
      return this.tenants.find(t => String(t.id) === String(id))?.name || '—';
    },
    minuteOptions() {
      return [30, 60, 120, 240];
    },
    canConfirm() {
      return !this.busy && (!this.isChoose || !!this.chosenTenantId) && (!this.needsWrite || this.reason.trim().length >= 5);
    }
  },
  created() {
    registerTenantAccessPrompt(request => this.open(request));
    // minden befejezett navigáció után (az elsőt is beleértve): a router-őr a linket a navigáció közben jegyzi meg
    this.$router.afterEach(() => this.enterFromLink());
  },
  methods: {
    open(request) {
      this.request = request;
      this.reason = '';
      this.chosenTenantId = null;
      this.minutes = 60;
      this.error = '';
      this.busy = false;
      this.show = true;
      return new Promise(resolve => {
        this.resolve = resolve;
      });
    },
    close(granted) {
      this.show = false;
      const resolve = this.resolve;
      this.resolve = null;
      resolve?.(granted);
    },
    async confirm() {
      if (!this.canConfirm) return;
      this.busy = true;
      this.error = '';
      try {
        if (this.isEnter) {
          await this.$store.dispatch('tenant/setCurrent', Number(this.enterTenantId));
        }
        if (this.needsWrite) {
          await this.$store.dispatch('tenant/enableWrite', {
            reason: this.reason,
            minutes: this.minutes
          });
        }
        await this.$store.dispatch('user/authenticated');
        await this.$store.dispatch('auth/permissions');
        this.close(true);
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('users.tenant_entry.write_failed');
        this.busy = false;
      }
    },
    /**
     * Egy másik intézmény lapjára mutató link (`?tenant=<id>`, a törlési kérelemé `&write=1`-gyel): belépés után
     * a lap a paraméter nélkül töltődik újra, hogy minden adata már az új intézményé legyen.
     */
    async enterFromLink() {
      if (!this.$store.getters['auth/authenticated']) return;
      const link = takeTenantEntryLink();
      if (!link?.target) return;
      await this.$store.dispatch('tenant/fetchMine').catch(() => {});
      if (!link.tenantId) {
        if (!this.requiresEntry || this.current) {
          this.$router.replace(link.target);
        } else if (await requestEntry(null)) {
          window.location.replace(link.target);
        }
        return;
      }
      if (!this.requiresEntry || String(this.current) === String(link.tenantId)) {
        this.$router.replace(link.target);
        return;
      }
      if (await requestEntry(link.tenantId, link.withWrite)) {
        window.location.replace(link.target);
      }
    }
  }
};